'use client';

import { Alert, Box, Stack, Typography } from '@mui/material';
import { useState } from 'react';

import { FormTextField } from '@/components/form/formTextField';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import type { SetupVpcPeeringWizardState } from '../setupVpcPeeringWizardState';

const AWS_ACCOUNT_ID_REGEX = /^\d{12}$/;
const AWS_VPC_ID_REGEX = /^vpc-[0-9a-f]{8,17}$/;
const CIDR_REGEX = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\/\d{1,2}$/;

const awsAccountIdValidation = {
  validateInput: (value: string) => !value || AWS_ACCOUNT_ID_REGEX.test(value),
  errorMessage: () => 'Must be a 12-digit AWS account ID',
};

const vpcIdValidation = {
  validateInput: (value: string) => !value || AWS_VPC_ID_REGEX.test(value),
  errorMessage: () => 'Must be a valid VPC ID (e.g. vpc-0123456789abcdef0)',
};

const cidrsValidation = {
  validateInput: (value: string) => {
    if (!value) return true;
    const cidrs = value
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    return cidrs.length > 0 && cidrs.every((c) => CIDR_REGEX.test(c));
  },
  errorMessage: () =>
    'Enter one or more valid CIDR blocks, separated by commas',
};

export const SetupVpcPeeringStep = (
  props: StepProps<SetupVpcPeeringWizardState>
) => {
  const dal = useDAL();
  const { wizardState: state, setWizardState } = props;
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = (partial: Partial<SetupVpcPeeringWizardState>) =>
    setWizardState({ ...state, ...partial });

  const parsedCidrs = state.cidrs
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const canCreate =
    AWS_ACCOUNT_ID_REGEX.test(state.awsAccountId) &&
    AWS_VPC_ID_REGEX.test(state.vpcId) &&
    parsedCidrs.length > 0 &&
    parsedCidrs.every((c) => CIDR_REGEX.test(c)) &&
    !creating;

  const handleCreate = async () => {
    setError(null);
    setCreating(true);
    try {
      await dal.lakehouse.createVpcPeering(state.workspaceId, {
        awsAccountId: state.awsAccountId,
        vpcId: state.vpcId,
        cidrs: parsedCidrs,
      });
      void props.finish('VPC peering initiated successfully');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to create VPC peering'
      );
    } finally {
      setCreating(false);
    }
  };

  return (
    <StepContainer
      hideStepper
      stepperIndex={0}
      canGoNext={() => canCreate}
      nextButtonText={creating ? 'Creating...' : 'Create'}
      nextButtonLoadingText='Creating...'
      backButtonText='Cancel'
      onBackClick={props.back}
      onNextClick={handleCreate}
      sx={{ overflow: 'auto', paddingX: '0px', paddingY: 'min(60px, 4vw)' }}
    >
      <Box paddingX='min(100px, 6vw)'>
        <Stack gap='24px' maxWidth='540px'>
          <Stack gap='8px'>
            <Typography variant='h6'>VPC Peering</Typography>
            <Typography variant='body2' color='text.secondary'>
              Configure VPC peering between your managed data lake and your AWS
              VPC.
            </Typography>
          </Stack>

          <FormTextField
            size='small'
            title='AWS Account ID'
            placeholder='123456789012'
            value={state.awsAccountId}
            onChange={(e) => update({ awsAccountId: e.target.value })}
            validation={awsAccountIdValidation}
          />

          <FormTextField
            size='small'
            title='VPC ID'
            placeholder='vpc-0123456789abcdef0'
            value={state.vpcId}
            onChange={(e) => update({ vpcId: e.target.value })}
            validation={vpcIdValidation}
          />

          <FormTextField
            size='small'
            title='CIDRs'
            placeholder='10.0.0.0/16, 172.16.0.0/12'
            value={state.cidrs}
            onChange={(e) => update({ cidrs: e.target.value })}
            validation={cidrsValidation}
          />
        </Stack>
      </Box>

      {error && (
        <Box paddingX='min(100px, 6vw)' paddingTop='24px'>
          <Alert severity='error' sx={{ maxWidth: '540px' }}>
            <Typography variant='body2'>{error}</Typography>
          </Alert>
        </Box>
      )}
    </StepContainer>
  );
};
